import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Smartphone, Download, Share, Plus, MoreVertical, 
  CheckCircle, ArrowRight, Apple, Chrome, Monitor,
  Camera, Bell, Wifi, WifiOff, Zap, X
} from 'lucide-react';

const InstallAppGuide = ({ onClose, isModal = false }) => {
  const [device, setDevice] = useState('android');
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Detect device
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDevice('ios');
    } else if (/android/.test(userAgent)) {
      setDevice('android');
    } else {
      setDevice('desktop');
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const features = [
    {
      icon: Camera,
      title: "Scanner IA",
      description: "Photographiez vos factures, l'IA extrait les données"
    },
    {
      icon: Bell,
      title: "Notifications",
      description: "Alertes de paiement et relances automatiques"
    },
    {
      icon: WifiOff,
      title: "Mode hors-ligne",
      description: "Consultez vos factures sans connexion"
    },
    {
      icon: Zap,
      title: "Accès rapide",
      description: "Lancez l'app en 1 clic depuis l'écran d'accueil"
    }
  ];

  const Container = isModal ? 'div' : Card;
  const containerProps = isModal ? { className: "bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" } : {};

  return (
    <Container {...containerProps}>
      {isModal && (
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-slate-900">Installer l'application mobile</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}
      
      <CardHeader className={isModal ? "pt-4" : ""}>
        {!isModal && (
          <>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-6 w-6 text-blue-600" />
              Application Mobile SmartFacture AI
            </CardTitle>
            <CardDescription>
              Installez notre app sur votre téléphone en 30 secondes
            </CardDescription>
          </>
        )}
        
        {isInstalled ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <p className="font-semibold text-green-800">Application déjà installée !</p>
              <p className="text-sm text-green-600">Vous utilisez SmartFacture AI en mode application</p>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-800 font-medium mb-2">
              Saviez-vous que SmartFacture AI fonctionne comme une vraie application mobile ?
            </p>
            <p className="text-sm text-blue-600">
              Pas besoin de télécharger sur l'App Store ou Google Play. 
              Installez directement depuis votre navigateur !
            </p>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Device selector */}
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
          <button
            onClick={() => setDevice('android')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-all ${
              device === 'android' ? 'bg-white shadow text-green-600' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Chrome className="h-5 w-5" />
            Android
          </button>
          <button
            onClick={() => setDevice('ios')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-all ${
              device === 'ios' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Apple className="h-5 w-5" />
            iPhone/iPad
          </button>
          <button
            onClick={() => setDevice('desktop')}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-all ${
              device === 'desktop' ? 'bg-white shadow text-blue-600' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Monitor className="h-5 w-5" />
            Ordinateur
          </button>
        </div>

        {/* Installation steps */}
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-900">
            Comment installer sur {device === 'android' ? 'Android' : device === 'ios' ? 'iPhone/iPad' : 'Ordinateur'} :
          </h3>

          {device === 'android' && (
            <div className="space-y-3">
              {deferredPrompt ? (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                  <p className="font-medium text-green-800 mb-3">Installation rapide disponible !</p>
                  <Button onClick={handleInstallClick} className="w-full bg-green-600 hover:bg-green-700">
                    <Download className="h-5 w-5 mr-2" />
                    Installer maintenant
                  </Button>
                </div>
              ) : (
                <>
                  <Step number={1} icon={MoreVertical}>
                    Appuyez sur les <strong>3 points</strong> en haut à droite de Chrome
                  </Step>
                  <Step number={2} icon={Download}>
                    Sélectionnez <strong>"Installer l'application"</strong> ou <strong>"Ajouter à l'écran d'accueil"</strong>
                  </Step>
                  <Step number={3} icon={CheckCircle}>
                    Confirmez l'installation. <strong>C'est fait !</strong>
                  </Step>
                </>
              )}
            </div>
          )}

          {device === 'ios' && (
            <div className="space-y-3">
              <Step number={1} icon={Share}>
                Appuyez sur le bouton <strong>Partager</strong> (carré avec flèche vers le haut) en bas de Safari
              </Step>
              <Step number={2} icon={Plus}>
                Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong>
              </Step>
              <Step number={3} icon={CheckCircle}>
                Appuyez sur <strong>"Ajouter"</strong> en haut à droite. <strong>C'est fait !</strong>
              </Step>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>Important :</strong> Utilisez Safari (pas Chrome) sur iPhone/iPad pour installer l'app.
                </p>
              </div>
            </div>
          )}

          {device === 'desktop' && (
            <div className="space-y-3">
              {deferredPrompt ? (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                  <p className="font-medium text-green-800 mb-3">Installation rapide disponible !</p>
                  <Button onClick={handleInstallClick} className="w-full bg-green-600 hover:bg-green-700">
                    <Download className="h-5 w-5 mr-2" />
                    Installer sur cet ordinateur
                  </Button>
                </div>
              ) : (
                <>
                  <Step number={1} icon={Chrome}>
                    Dans Chrome, cliquez sur l'icône <strong>d'installation</strong> dans la barre d'adresse (à droite)
                  </Step>
                  <Step number={2} icon={Download}>
                    Ou cliquez sur les <strong>3 points → Installer SmartFacture AI</strong>
                  </Step>
                  <Step number={3} icon={CheckCircle}>
                    L'app s'ouvrira dans sa propre fenêtre. <strong>C'est fait !</strong>
                  </Step>
                </>
              )}
            </div>
          )}
        </div>

        {/* Features */}
        <div className="pt-4 border-t">
          <h3 className="font-semibold text-slate-900 mb-4">Ce que vous pouvez faire avec l'app :</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <feature.icon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{feature.title}</p>
                  <p className="text-sm text-slate-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="pt-4 border-t">
          <h3 className="font-semibold text-slate-900 mb-3">Questions fréquentes</h3>
          <div className="space-y-3">
            <FAQ 
              question="C'est quoi la différence avec une app normale ?"
              answer="Aucune ! Notre app utilise la technologie PWA (Progressive Web App) qui offre la même expérience qu'une app native, sans passer par l'App Store. C'est plus rapide et ne prend presque pas d'espace sur votre téléphone."
            />
            <FAQ 
              question="Est-ce sécurisé ?"
              answer="Absolument ! La connexion est chiffrée (HTTPS), vos données sont protégées comme sur le site web. L'app ne peut pas accéder à vos autres applications ou données personnelles."
            />
            <FAQ 
              question="Combien d'espace ça prend ?"
              answer="Moins de 5 Mo ! Contrairement aux apps classiques qui peuvent prendre 50-200 Mo, notre PWA est ultra-légère."
            />
          </div>
        </div>
      </CardContent>
    </Container>
  );
};

// Step component
const Step = ({ number, icon: Icon, children }) => (
  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
      {number}
    </div>
    <div className="flex-1">
      <p className="text-slate-700">{children}</p>
    </div>
    <Icon className="h-6 w-6 text-slate-400 flex-shrink-0" />
  </div>
);

// FAQ component
const FAQ = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-medium text-slate-900">{question}</span>
        <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3">
          <p className="text-sm text-slate-600">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default InstallAppGuide;
